import { useEffect, useRef, useState } from "react";

import { addDays } from "date-fns";

import { WAREHOUSE_ADDRESS } from "~/constants/warehouse";
import { useToast } from "~/hooks/use-toast";
import { useClientCreditUnlock } from "~/hooks/useClientCreditUnlock";
import { useGeolocation } from "~/hooks/useGeolocation";
import { supabase } from "~/integrations/supabase/client";
import { linkDocumentsToSalesRequest } from "~/services/salesRequestDocumentService";
import { useProfileStore } from "~/store/profileStore";
import { Address, Client, SalesRequestDocument } from "~/types/client";
import { OrderItem } from "~/types/product";
import { isPickupAddress } from "~/utils/addressUtils";
import {
  calculateCreditUtilization,
  validate7DayCreditEligibility,
} from "~/utils/creditUtilizationUtils";
import {
  FLETE_PRODUCT_ID,
  isRemoteRegion,
  requiresFlete,
} from "~/utils/fleteUtils";
import {
  getProtestedChecksBlockMessage,
  validateClientForProtestedChecks,
} from "~/utils/protestedChecksUtils";

// Extended OrderItem with suggestedPrice and price range info
interface ExtendedOrderItem extends OrderItem {
  suggestedPrice: number;
  hasPriceRanges: boolean;
}

interface OrderSubmissionProps {
  client: Client;
  selectedAddress: Address | null;
  selectedAddressId: string | null;
  orderItems: ExtendedOrderItem[];
  total: number;
  comments: string;
  internalNotes?: string;
  facturaObservations?: string;
  deliveryOption: "asap" | "date";
  deliveryDate: Date | undefined;
  requestedDeliveryDateStart: Date | undefined;
  requestedDeliveryDateEnd: Date | undefined;
  refetchCreditUtilization?: () => void;
  hasPriceBelowSuggested?: boolean;
  documents?: SalesRequestDocument[];
  // No-venta (muestra / traslado): the order moves stock but is not a sale.
  isNonSale?: boolean;
}

export const useOrderSubmission = ({
  client,
  selectedAddress,
  selectedAddressId,
  orderItems,
  total,
  comments,
  internalNotes,
  facturaObservations,
  deliveryOption,
  deliveryDate,
  requestedDeliveryDateStart,
  requestedDeliveryDateEnd,
  refetchCreditUtilization,
  hasPriceBelowSuggested = false,
  documents = [],
  isNonSale = false,
}: OrderSubmissionProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Defense-in-depth guard against re-entrant JS double-fires of the submit
  // handler. The real idempotency guarantee lives in the
  // create_sales_request_atomic RPC (which also covers transport-level retries).
  const submitInProgressRef = useRef(false);
  const [clientSalesConditionId, setClientSalesConditionId] = useState<
    string | null
  >(null);
  const [salesConditionName, setSalesConditionName] = useState<string | null>(
    null
  );
  const [salesConditionMaxDays, setSalesConditionMaxDays] = useState<
    number | null
  >(null);
  const { toast } = useToast();
  const { id: profileId, role: userRole } = useProfileStore();
  const {
    getGeolocation,
    latitude,
    longitude,
    loading: geoLoading,
  } = useGeolocation();

  // Hook for checking protested checks unlock status
  const { isUnlocked: isProtestedChecksUnlocked } = useClientCreditUnlock(
    String(client.id),
    "protested_checks"
  );

  // Hook for checking overdue debt unlock status
  const { isUnlocked: isOverdueDebtUnlocked, refetch: refetchOverdueUnlock } =
    useClientCreditUnlock(String(client.id), "overdue_debt");

  // Check if the current user is a seller
  const isSeller = userRole === "seller";

  // Debug documents when the hook is initialized or documents changes
  useEffect(() => {
    console.log("useOrderSubmission received documents:", documents);
  }, [documents]);

  useEffect(() => {
    const fetchClientSalesCondition = async () => {
      try {
        // Use a simple select and then access fields with type assertion
        const { data, error } = await supabase
          .from("clients")
          .select("*")
          .eq("id", String(client.id))
          .is("deleted_at", null) // Exclude soft-deleted clients
          .single();

        if (error) throw error;

        // Safely access the data with type assertion
        const salesConditionId = (data as any)?.sales_condition_id || null;
        setClientSalesConditionId(salesConditionId);

        // If we have a sales condition ID, fetch its name and maximum_days
        if (salesConditionId) {
          const { data: conditionData, error: conditionError } = await supabase
            .from("sales_conditions")
            .select("*")
            .eq("id", salesConditionId)
            .single();

          if (!conditionError && conditionData) {
            // Type assert to safely access the fields
            setSalesConditionName((conditionData as any).name || null);
            setSalesConditionMaxDays(
              (conditionData as any).maximum_days || null
            );
          }
        }
      } catch (err) {
        console.error("Error fetching client sales condition:", err);
      }
    };

    fetchClientSalesCondition();
  }, [client.id]);

  const runSaveOrder = async (
    onSuccess: (
      status?:
        | "pending"
        | "pending_pricing"
        | "pending_credit"
        | "prepaid"
        | "accepted"
    ) => void
  ) => {
    // Log the documents received in handleSaveOrder
    console.log(
      "useOrderSubmission.handleSaveOrder: Documents received",
      documents
    );

    if (!profileId) {
      toast({
        title: "Error",
        description: "No se ha podido identificar el usuario actual",
        variant: "destructive",
      });
      return;
    }

    if (!selectedAddress && selectedAddressId) {
      toast({
        title: "Error",
        description: "No se ha podido cargar la dirección seleccionada",
        variant: "destructive",
      });
      return;
    }

    // Check for protested checks that would block sales request creation
    // Skip validation if client has an active unlock
    if (!isProtestedChecksUnlocked) {
      const protestedCheckValidation = await validateClientForProtestedChecks(
        String(client.id)
      );
      if (!protestedCheckValidation.canCreateSalesRequest) {
        const blockMessage = getProtestedChecksBlockMessage(
          protestedCheckValidation.protestedChecks || []
        );
        toast({
          title: "Cliente bloqueado",
          description: blockMessage,
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
    }

    // Check for overdue facturas that would block sales request creation
    // Skip validation if client has an active overdue_debt unlock
    if (!isOverdueDebtUnlocked) {
      // Fetch overdue unpaid facturas
      const { data: overdueFacturas, error: overdueError } = await supabase
        .from("sales_request_documents")
        .select(
          `
          *,
          sales_request:sales_request_id(
            id,
            paid,
            payment_due_date
          )
        `
        )
        .eq("client_id", String(client.id))
        .eq("document_type", "factura")
        .is("cancelled", false)
        .is("deleted_at", null)
        .not("bsale_number", "is", null);

      if (!overdueError && overdueFacturas) {
        // Filter for overdue unpaid invoices
        const overdueUnpaidFacturas = overdueFacturas.filter((doc: any) => {
          const sr = doc.sales_request;
          return (
            sr &&
            sr.paid === false &&
            sr.payment_due_date &&
            new Date(sr.payment_due_date) < new Date()
          );
        });

        if (overdueUnpaidFacturas.length > 0) {
          toast({
            title: "Cliente con deuda vencida",
            description: `Este cliente tiene ${overdueUnpaidFacturas.length} factura(s) vencida(s) sin pagar. No se pueden crear nuevas notas de venta hasta que se regularice la situación.`,
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }
      }
    }

    // Check if client is active
    const { data: clientStatusData, error: clientStatusError } = await supabase
      .from("clients")
      .select("is_active, deactivation_reason")
      .eq("id", String(client.id))
      .single();

    if (clientStatusError || !clientStatusData) {
      console.error("Error checking client status:", clientStatusError);
      toast({
        title: "Error",
        description: "No se pudo verificar el estado del cliente.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    if (!clientStatusData.is_active) {
      toast({
        title: "Cliente inactivo",
        description: `No se puede crear una nota de venta para un cliente inactivo. ${
          clientStatusData.deactivation_reason
            ? `Razón: ${clientStatusData.deactivation_reason}`
            : ""
        }`,
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    // Validate FLETE is present when required
    const isPickup = isPickupAddress(selectedAddressId);
    const deliveryState = selectedAddress?.state;
    const fleteIsRequired = !isPickup && requiresFlete(deliveryState);
    const hasFleteInOrder = orderItems.some(
      (item) => item.product.id === FLETE_PRODUCT_ID
    );

    if (fleteIsRequired && !hasFleteInOrder) {
      toast({
        title: "Flete requerido",
        description:
          "Esta orden requiere el producto FLETE porque la dirección de entrega está fuera de la Región Metropolitana, Valparaíso y O'Higgins.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);

    // Request geolocation coordinates
    let locationCoordinates = { latitude: null, longitude: null };
    try {
      // Get location coordinates
      await getGeolocation()
        .then((position) => {
          locationCoordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
        })
        .catch((error) => {
          console.error("Error getting location:", error);
          // Continue without coordinates if there's an error
        });

      const paymentTerms = client.trailingPaymentDays || 30;
      const now = new Date();
      const paymentDueDate = addDays(now, paymentTerms);

      // Fetch client risk status, financial information, and sales_condition_id
      // Note: We fetch sales_condition_id here (not just from useEffect state) to avoid race conditions
      // where the form is submitted before the useEffect completes
      const { data: clientData, error: clientError } = await supabase
        .from("clients")
        .select(
          "insured_amount, risk_checked, trailing_payment_days, sales_condition_id"
        )
        .eq("id", String(client.id))
        .single();

      if (clientError) {
        throw new Error("Error fetching client data: " + clientError.message);
      }

      // Use the freshly fetched sales_condition_id to avoid race conditions
      const fetchedSalesConditionId =
        (clientData as any)?.sales_condition_id || null;

      // Fetch sales condition details if we have an ID
      let fetchedSalesConditionMaxDays: number | null = null;
      if (fetchedSalesConditionId) {
        const { data: conditionData } = await supabase
          .from("sales_conditions")
          .select("maximum_days")
          .eq("id", fetchedSalesConditionId)
          .single();

        if (conditionData) {
          fetchedSalesConditionMaxDays =
            (conditionData as any).maximum_days || null;
        }
      }

      // Check if client has a 7-day credit sales condition
      const has7DayCredit = fetchedSalesConditionMaxDays === 7;

      if (has7DayCredit) {
        // Validate 7-day credit eligibility using the dedicated utility function
        const validation = await validate7DayCreditEligibility(
          String(client.id),
          fetchedSalesConditionMaxDays
        );

        if (!validation.canCreate) {
          let errorMessage = "No se puede crear la nota de venta.";

          if (validation.reason === "overdue_payments") {
            errorMessage = `Este cliente tiene ${validation.overdueCount} pago(s) vencido(s) con crédito a 7 días. Debe cancelar los pagos vencidos antes de crear nuevas notas de venta.`;
          } else if (validation.reason === "too_many_concurrent_orders") {
            const currentCount = (validation as any).currentCount || 0;
            const maxAllowed = (validation as any).maxAllowed || 0;
            errorMessage = `Este cliente ya tiene ${currentCount} órdenes pendientes. Máximo permitido: ${maxAllowed}.`;
          } else if (validation.reason === "validation_error") {
            errorMessage =
              "Error al validar el estado del cliente. Por favor intente nuevamente.";
          }

          toast({
            title: "No se puede crear la nota de venta",
            description: errorMessage,
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }

        // Optional: Log current unpaid count for monitoring
        if (
          validation.currentUnpaidCount &&
          validation.currentUnpaidCount > 0
        ) {
          console.log(
            `Cliente has ${validation.currentUnpaidCount} unpaid orders within 7-day window`
          );
        }
      }

      // Check for specific sales condition price limit validation
      const restrictedSalesConditionId = "d1d384ec-12d7-4e38-baa2-0b943adc8d6c";
      const priceWithIva = Math.round(total * 1.19);
      const maxAllowedPriceWithIva = 20000000;

      // Check if either the sales request or client has the restricted sales condition
      const hasRestrictedSalesCondition =
        fetchedSalesConditionId === restrictedSalesConditionId;

      if (
        hasRestrictedSalesCondition &&
        priceWithIva > maxAllowedPriceWithIva
      ) {
        toast({
          title: "No se puede crear la nota de venta",
          description: `El precio con IVA ($${priceWithIva.toLocaleString("es-CL")}) excede el límite máximo permitido de $${maxAllowedPriceWithIva.toLocaleString("es-CL")} para esta condición de venta.`,
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      let availableCredit = 0;
      if (!clientError && clientData?.insured_amount != null) {
        try {
          const creditUtilizationData = await calculateCreditUtilization(
            String(client.id)
          );
          availableCredit = creditUtilizationData.available;
        } catch (creditError) {
          console.error("Error calculating credit utilization:", creditError);
          // Fallback to old calculation if new one fails
          const { data: salesRequestsData, error: salesRequestsError } =
            await supabase
              .from("sales_request")
              .select("total_amount")
              .eq("client_id", String(client.id))
              .in("status", ["accepted", "in_transit", "delivered"])
              .eq("paid", false);

          let utilizedCredit = 0;
          if (!salesRequestsError && Array.isArray(salesRequestsData)) {
            utilizedCredit = salesRequestsData.reduce(
              (total, request) => total + (Number(request.total_amount) || 0),
              0
            );
          }
          availableCredit = Number(clientData.insured_amount) - utilizedCredit;
        }
      }

      // NEW WORKFLOW: Determine the initial status based on pricing and sales conditions
      let statusToInsert:
        | "pending"
        | "pending_pricing"
        | "pending_credit"
        | "prepaid"
        | "accepted" = "pending";

      // Calculate if any items have pricing warnings (price outside of suggested range)
      const hasPricingIssue = orderItems.some((item) => {
        // Check if the item has a suggested price and unit price is below it
        return item.suggestedPrice && item.unitPrice < item.suggestedPrice;
      });

      // Check if any items don't have price ranges
      const hasItemsWithoutPriceRanges = orderItems.some((item) => {
        return !item.hasPriceRanges; // Check the explicit flag
      });

      // Check if delivery is to a remote region (outside RM, Valparaíso, O'Higgins)
      const isPickupOrder = selectedAddressId === "PICKUP";
      const isDeliveryToRemoteRegion =
        !isPickupOrder && isRemoteRegion(selectedAddress?.state);

      // STEP 1: Check if delivery is to remote region, price falls out of range, OR items don't have price ranges (requires sales team pricing approval)
      if (
        isDeliveryToRemoteRegion ||
        hasPricingIssue ||
        hasItemsWithoutPriceRanges
      ) {
        statusToInsert = "pending_pricing"; // Pendiente de autorización (sales team approves pricing)
      } else {
        // STEP 2: Price is in range, check sales condition

        // Get the sales condition details to check if it's TRANSFERENCIA ANTICIPADA
        const { data: salesConditionData, error: salesConditionError } =
          await supabase
            .from("sales_conditions")
            .select("name, requires_prepayment, type")
            .eq("id", fetchedSalesConditionId || "")
            .single();

        let isTransferenciaAnticipada = false;
        if (!salesConditionError && salesConditionData) {
          // Check if this is TRANSFERENCIA ANTICIPADA sales condition
          isTransferenciaAnticipada =
            salesConditionData.name === "TRANSFERENCIA ANTICIPADA" ||
            salesConditionData.name
              ?.toLowerCase()
              .includes("transferencia anticipada") ||
            Boolean(salesConditionData.requires_prepayment) ||
            salesConditionData.type === "prepaid";
        }

        if (isTransferenciaAnticipada) {
          // For TRANSFERENCIA ANTICIPADA: → Anticipado pendiente de pago
          statusToInsert = "prepaid";
        } else {
          // For all other sales conditions: → Autorización cobranza
          statusToInsert = "pending";
        }

        // Additional checks for edge cases
        // If client is not risk checked, override to prepaid regardless of sales condition
        if (!clientData.risk_checked) {
          statusToInsert = "prepaid";
        }
        // If client has insured_amount=0 and trailing_payment_days=0, override to prepaid
        else if (
          clientData.risk_checked &&
          clientData.insured_amount === 0 &&
          clientData.trailing_payment_days === 0
        ) {
          statusToInsert = "prepaid";
        }
        // Check for credit limit issues (only if not already set to prepaid/pending_pricing)
        else if (
          availableCredit > 0 &&
          total > availableCredit * 1.2 &&
          statusToInsert === "pending"
        ) {
          statusToInsert = "pending_credit";
        }
      }

      // No-venta (muestra / traslado): not a sale, so it skips pricing and
      // cobranza approval and is born directly in the dispatchable "accepted"
      // state. Emitting its guía (shippingTypeId=6) then moves it to in_transit,
      // which decrements stock via the existing dispatch trigger. It never gets
      // a factura (guarded server-side in create-factura).
      if (isNonSale) {
        statusToInsert = "accepted";
      }

      // Handle pickup vs delivery address logic
      const isPickup = selectedAddressId === "PICKUP";
      let deliveryAddressData = {};

      if (isPickup) {
        // For pickup orders, set address_id to null and use warehouse address
        deliveryAddressData = {
          delivery_address_id: null,
          delivery_street: WAREHOUSE_ADDRESS.street,
          delivery_number: WAREHOUSE_ADDRESS.number,
          delivery_complement: `${WAREHOUSE_ADDRESS.name} - Cliente retira en bodega`,
          delivery_city: WAREHOUSE_ADDRESS.city,
          delivery_state: WAREHOUSE_ADDRESS.region,
          delivery_comuna: WAREHOUSE_ADDRESS.comuna,
        };
      } else {
        // For regular delivery, use the selected address
        deliveryAddressData = {
          delivery_address_id: selectedAddressId || null,
          delivery_street: selectedAddress?.street || null,
          delivery_number: selectedAddress?.number || null,
          delivery_complement: selectedAddress?.complement || null,
          delivery_city: selectedAddress?.city || null,
          delivery_state: selectedAddress?.state || null,
          delivery_comuna: selectedAddress?.comuna || null,
        };
      }

      // Calculate if any items have pricing warnings (unit price <= suggested price range)
      const hasPricingWarning = orderItems.some((item) => {
        // Check if the item has a suggested price and unit price is at or below it
        return item.suggestedPrice && item.unitPrice <= item.suggestedPrice;
      });

      const salesRequestData = {
        client_id: String(client.id),
        created_by: profileId,
        status: statusToInsert,
        payment_terms: paymentTerms,
        payment_type: "future",
        payment_due_date: paymentDueDate.toISOString(),
        total_amount: total,
        has_pricing_warning: hasPricingWarning, // New field to track pricing warnings
        comments: comments || null,
        internal_notes: internalNotes || null,
        factura_observations: facturaObservations || null,
        delivery_date: deliveryDate ? deliveryDate.toISOString() : null,
        requested_delivery_date_start: requestedDeliveryDateStart
          ? requestedDeliveryDateStart.toISOString()
          : null,
        requested_delivery_date_end: requestedDeliveryDateEnd
          ? requestedDeliveryDateEnd.toISOString()
          : null,
        sales_condition_id: fetchedSalesConditionId,
        ...deliveryAddressData,
        creation_lat: locationCoordinates.latitude,
        creation_lng: locationCoordinates.longitude,
        is_non_sale: isNonSale,
      };

      // Client-generated id makes creation idempotent: any transport-level
      // retry of the create carries the same id, so it collapses to a no-op
      // instead of minting a duplicate empty order.
      const newSalesRequestId = crypto.randomUUID();

      const salesRequestItems = orderItems.map((item) => ({
        product_id: String(item.product.id),
        quantity: item.quantity,
        unit_price: item.unitPrice,
        discount_percent: item.discountPercent ?? 0,
      }));

      // Atomic create: header + items in a single transaction via
      // create_sales_request_atomic (ON CONFLICT (id) DO NOTHING). was_created
      // false means a retry hit the already-created row — still a success.
      const { error: rpcError } = await supabase.rpc(
        "create_sales_request_atomic",
        {
          p_id: newSalesRequestId,
          p_header: salesRequestData,
          p_items: salesRequestItems,
        }
      );

      if (rpcError) throw rpcError;

      // Link any temporary documents to the sales request
      if (documents && documents.length > 0) {
        console.log("useOrderSubmission: About to link documents", {
          documentCount: documents.length,
          salesRequestId: newSalesRequestId,
          documents,
        });
        try {
          console.log(
            `Linking ${documents.length} documents to sales request ${newSalesRequestId}`
          );
          const linkResult = await linkDocumentsToSalesRequest(
            documents,
            newSalesRequestId
          );
          if (!linkResult) {
            console.warn(
              "Documents were uploaded but could not be linked to the sales request. " +
                "They are still available in storage but may not appear in the document list."
            );
            toast({
              title: "Advertencia",
              description:
                "Algunos documentos no pudieron ser asociados a la nota de venta. El equipo técnico ha sido notificado.",
              variant: "destructive",
            });
          } else {
            console.log("Documents successfully linked to sales request");
          }
        } catch (docError) {
          console.error("Error linking documents to sales request:", docError);
          // Don't throw - continue with the process even if linking fails
          toast({
            title: "Advertencia",
            description:
              "Algunos documentos no pudieron ser asociados correctamente. Por favor contacte a soporte si no ve sus documentos adjuntos.",
            variant: "destructive",
          });
        }
      }

      // Automatically generate factura for accepted sales requests
      // NOTE: This section is disabled as automatic approval has been removed
      /*
      if (statusToInsert === "accepted") {
        try {
          // Get the anon key from the Supabase client (safe approach)
          const { data: sessionData } = await supabase.auth.getSession();
          const anonKey = sessionData?.session?.access_token || "";

          // Prepare an authorization header
          const authHeader = import.meta.env.VITE_SUPABASE_KEY || anonKey;

          // Call the Supabase Edge Function directly
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL || "https://mdrvhekhimhcwydrpueo.supabase.co"}/functions/v1/create-factura`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authHeader}`,
              },
              body: JSON.stringify({ salesRequestId: salesRequest.id }),
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            console.warn("Factura creation failed:", errorText);
          } else {
            console.log("Factura created successfully");
          }
        } catch (error) {
          console.error("Error generating factura:", error);
        }
      }
      */

      // Success message depending on status
      let statusMessage = "Nota de venta creada exitosamente";
      if (isNonSale) {
        statusMessage =
          "Nota de traslado (no venta) creada. Genere la guía para rebajar el stock. No genera factura ni deuda.";
      } else if (statusToInsert === "pending_pricing") {
        if (isDeliveryToRemoteRegion) {
          statusMessage =
            "Nota de venta creada. Requiere autorización del equipo de ventas por entrega fuera de RM/Valparaíso/O'Higgins.";
        } else if (hasItemsWithoutPriceRanges && hasPricingIssue) {
          statusMessage =
            "Nota de venta creada. Requiere autorización del equipo de ventas por productos sin rangos de precio y precios fuera de rango.";
        } else if (hasItemsWithoutPriceRanges) {
          statusMessage =
            "Nota de venta creada. Requiere autorización del equipo de ventas por productos sin rangos de precio.";
        } else {
          statusMessage =
            "Nota de venta creada. Requiere autorización del equipo de ventas por precios fuera de rango.";
        }
      } else if (statusToInsert === "prepaid") {
        statusMessage =
          "Nota de venta creada. El vendedor debe registrar el pago anticipado.";
      } else if (statusToInsert === "pending_credit") {
        statusMessage =
          "Nota de venta creada. Requiere aprobación por exceder el límite de crédito disponible.";
      } else if (statusToInsert === "pending") {
        statusMessage =
          "Nota de venta creada. Pendiente de autorización del equipo de cobranza.";
      } else {
        statusMessage = "Nota de venta creada exitosamente.";
      }

      toast({
        title: "Éxito",
        description: statusMessage,
      });

      onSuccess(statusToInsert);

      // NOTE: Credit utilization refresh for auto-accepted orders is disabled
      // since automatic approval has been removed
      /*
      if (statusToInsert === "accepted" && refetchCreditUtilization) {
        refetchCreditUtilization();
      }
      */
    } catch (error: unknown) {
      console.error("Error saving sales request:", error);
      toast({
        title: "Error al crear la nota de venta",
        description:
          (error as Error)?.message ||
          "Ha ocurrido un error al crear la nota de venta",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Public entry point. Holds an in-flight lock across the ENTIRE submission
  // (including the async validations inside runSaveOrder) so repeated clicks
  // during a slow request cannot each create a full duplicate order. Each click
  // mints a new id, which the idempotent create RPC cannot dedupe — so this
  // lock, not the RPC, is what stops multi-click duplicates.
  const handleSaveOrder = async (
    onSuccess: (
      status?:
        | "pending"
        | "pending_pricing"
        | "pending_credit"
        | "prepaid"
        | "accepted"
    ) => void
  ) => {
    if (submitInProgressRef.current) {
      return;
    }
    submitInProgressRef.current = true;
    setIsSubmitting(true);
    try {
      await runSaveOrder(onSuccess);
    } finally {
      submitInProgressRef.current = false;
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    // Delivery date + requested time window are mandatory for every role, except
    // no-venta (muestra / traslado) orders, which have no delivery commitment.
    const deliveryValid =
      isNonSale ||
      (!!deliveryDate &&
        !!requestedDeliveryDateStart &&
        !!requestedDeliveryDateEnd);

    const basicValidation =
      orderItems.length > 0 && !!selectedAddressId && deliveryValid;

    return basicValidation;
  };

  return {
    handleSaveOrder,
    isFormValid,
    isSubmitting,
    refetchOverdueUnlock,
  };
};
